import React from 'react';
import styled from 'styled-components';
import { Spacing12, Spacing16 } from 'autogen/design-tokens/tokens';
import type { DesignThemeName } from 'autogen/swagger/all';
import { backgroundStripes } from 'modules/helpers/style';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint, sizes } from 'variables';

type StripedShadow = {
    designThemeName: DesignThemeName;
    transparentSecondColor: boolean;
};

const stripeWidthMobile = Spacing12;
const stripeWidthDesktop = Spacing16;

function getClipPath(width) {
    return `
        polygon(calc(100% - ${width}) 0, 100% 0, 100% 100%, 0 100%, 0 calc(100% - ${width}), calc(100% - ${width}) calc(100% - ${width}))
    `;
}

const StripesWrapper = styled.div<Pick<StripedShadow, 'transparentSecondColor'>>`
    position: relative;
    padding: 0 ${stripeWidthMobile} ${stripeWidthMobile} 0;

    ${breakpoint.up(sizes.md)} {
        padding: 0 ${stripeWidthDesktop} ${stripeWidthDesktop} 0;
    }

    &:before {
        content: '';
        position: absolute;
        top: ${stripeWidthMobile};
        left: ${stripeWidthMobile};
        clip-path: ${getClipPath(stripeWidthMobile)};
        bottom: 0;
        right: 0;
        ${({ theme, transparentSecondColor }) =>
            backgroundStripes({
                color1: theme.ColorBackgroundPrimary,
                color2: transparentSecondColor ? 'transparent' : theme.ColorBackgroundSecondary,
                angle: '-45deg'
            })}

        ${breakpoint.up(sizes.md)} {
            top: ${stripeWidthDesktop};
            left: ${stripeWidthDesktop};
            clip-path: ${getClipPath(stripeWidthDesktop)};
        }
    }
`;

export default function WithStripedShadow<T extends Record<string, any>>(WrappedComponent: React.ComponentType<T>) {
    return React.forwardRef((props: T & StripedShadow, ref) => {
        const { designThemeName, transparentSecondColor } = props;

        return (
            <ThemeProvider theme={designThemeName}>
                <StripesWrapper transparentSecondColor={transparentSecondColor}>
                    <WrappedComponent {...props} ref={ref} />
                </StripesWrapper>
            </ThemeProvider>
        );
    });
}
